import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ScienceIcon from "@mui/icons-material/Science";
import RadioIcon from "@mui/icons-material/Radio";
import RefreshIcon from "@mui/icons-material/Refresh";
import MedicationIcon from "@mui/icons-material/Medication";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

export default function PacCpoeSection({
  cpoeOrders = [],
  loadingCpoeOrders = false,
  onRefreshCpoe,
  onOpenOrderDrawer,
  onPrev,
  onNext,
}) {
  const [cpoeDeptFilter, setCpoeDeptFilter] = useState("ALL");
  const [cpoeStatusFilter, setCpoeStatusFilter] = useState("ALL");
  const [cpoeSearch, setCpoeSearch] = useState("");
  const [selectedCpoeReport, setSelectedCpoeReport] = useState(null);

  const labCount = useMemo(
    () => cpoeOrders.filter((o) => o.type === "Laboratory").length,
    [cpoeOrders]
  );
  const radCount = useMemo(
    () => cpoeOrders.filter((o) => o.type === "Radiology").length,
    [cpoeOrders]
  );

  const filteredCpoeOrders = useMemo(() => {
    return cpoeOrders.filter((o) => {
      if (cpoeDeptFilter !== "ALL" && o.type !== cpoeDeptFilter) return false;
      if (cpoeStatusFilter !== "ALL") {
        const s = String(o.status || "").toLowerCase();
        if (
          cpoeStatusFilter === "REPORTED" &&
          !s.includes("report") &&
          !s.includes("complete")
        )
          return false;
        if (
          cpoeStatusFilter === "IN_PROGRESS" &&
          !s.includes("progress") &&
          !s.includes("collect")
        )
          return false;
        if (
          cpoeStatusFilter === "PENDING" &&
          !s.includes("pend") &&
          !s.includes("order")
        )
          return false;
      }
      if (cpoeSearch.trim()) {
        const q = cpoeSearch.toLowerCase();
        const matchName = String(o.service_name || "").toLowerCase().includes(q);
        const matchNo = String(o.order_no || "").toLowerCase().includes(q);
        const matchDoc = String(o.doctor_name || "").toLowerCase().includes(q);
        if (!matchName && !matchNo && !matchDoc) return false;
      }
      return true;
    });
  }, [cpoeOrders, cpoeDeptFilter, cpoeStatusFilter, cpoeSearch]);

  return (
    <Stack spacing={2.5}>
      {/* Top action header card */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          bgcolor: "#f0fdf4",
          border: "1px solid #bbf7d0",
          borderRadius: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <ScienceIcon sx={{ color: "#16a34a", fontSize: 28 }} />
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#166534" }}>
              Computerized Physician Order Entry (CPOE) & Diagnostic Results Feed
            </Typography>
            <Typography variant="caption" sx={{ color: "#15803d" }}>
              All pre-operative laboratory tests, radiology imaging studies, and validated reports are displayed live from the hospital diagnostic systems. Manual transcription is not required.
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon />}
            disabled={loadingCpoeOrders}
            onClick={onRefreshCpoe}
            sx={{ bgcolor: "#fff", textTransform: "none", fontWeight: 700 }}
          >
            {loadingCpoeOrders ? "Refreshing..." : "Refresh Results"}
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<MedicationIcon />}
            onClick={onOpenOrderDrawer}
            sx={{ bgcolor: "#0f766e", textTransform: "none", fontWeight: 700 }}
          >
            Open Order Management CPOE
          </Button>
        </Stack>
      </Paper>

      {/* Filter Toolbar */}
      <Paper elevation={0} sx={{ p: 1.5, border: "1px solid #e2e8f0", borderRadius: 2, bgcolor: "#fff" }}>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} sm={4}>
            <Stack direction="row" spacing={1}>
              <Chip
                clickable
                label={`All Orders (${cpoeOrders.length})`}
                color={cpoeDeptFilter === "ALL" ? "primary" : "default"}
                onClick={() => setCpoeDeptFilter("ALL")}
                sx={{ fontWeight: 700 }}
              />
              <Chip
                clickable
                icon={<ScienceIcon />}
                label={`Lab (${labCount})`}
                color={cpoeDeptFilter === "Laboratory" ? "primary" : "default"}
                onClick={() => setCpoeDeptFilter("Laboratory")}
                sx={{ fontWeight: 700 }}
              />
              <Chip
                clickable
                icon={<RadioIcon />}
                label={`Radiology (${radCount})`}
                color={cpoeDeptFilter === "Radiology" ? "primary" : "default"}
                onClick={() => setCpoeDeptFilter("Radiology")}
                sx={{ fontWeight: 700 }}
              />
            </Stack>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              fullWidth
              size="small"
              label="Filter by Status"
              value={cpoeStatusFilter}
              onChange={(e) => setCpoeStatusFilter(e.target.value)}
            >
              <MenuItem value="ALL">All Statuses</MenuItem>
              <MenuItem value="REPORTED">Reported / Completed</MenuItem>
              <MenuItem value="IN_PROGRESS">In Progress / Sample Collected</MenuItem>
              <MenuItem value="PENDING">Pending / Ordered</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search test name or order number..."
              value={cpoeSearch}
              onChange={(e) => setCpoeSearch(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: "#94a3b8", fontSize: 18, mr: 0.5 }} />,
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Orders & Results Table */}
      <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: 2 }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: "#f1f5f9" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 800, color: "#1e293b", py: 1.5 }}>Order #</TableCell>
              <TableCell sx={{ fontWeight: 800, color: "#1e293b" }}>Department</TableCell>
              <TableCell sx={{ fontWeight: 800, color: "#1e293b" }}>Investigation / Service</TableCell>
              <TableCell sx={{ fontWeight: 800, color: "#1e293b" }}>Priority</TableCell>
              <TableCell sx={{ fontWeight: 800, color: "#1e293b" }}>Ordering Physician</TableCell>
              <TableCell sx={{ fontWeight: 800, color: "#1e293b" }}>Ordered Time</TableCell>
              <TableCell sx={{ fontWeight: 800, color: "#1e293b" }}>Status</TableCell>
              <TableCell align="center" sx={{ fontWeight: 800, color: "#1e293b" }}>Diagnostic Results</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loadingCpoeOrders ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                  <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
                    Fetching CPOE Orders & Results...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : filteredCpoeOrders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <ScienceIcon sx={{ fontSize: 40, color: "#cbd5e1", mb: 1 }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    No diagnostic orders found matching criteria for this patient's admission.
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    Click 'Open Order Management CPOE' to prescribe laboratory tests or radiological imaging.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredCpoeOrders.map((order) => {
                const statusLower = String(order.status || "").toLowerCase();
                const isReported = statusLower.includes("report") || statusLower.includes("complete");
                const isInProgress = statusLower.includes("progress") || statusLower.includes("collect");
                return (
                  <TableRow key={order.id} hover sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                    <TableCell sx={{ fontWeight: 700, color: "#0f172a" }}>{order.order_no}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        icon={order.type === "Laboratory" ? <ScienceIcon sx={{ fontSize: 14 }} /> : <RadioIcon sx={{ fontSize: 14 }} />}
                        label={order.type}
                        sx={{
                          height: 22,
                          fontSize: "0.72rem",
                          fontWeight: 700,
                          bgcolor: order.type === "Laboratory" ? "#ecfeff" : "#fdf4ff",
                          color: order.type === "Laboratory" ? "#0891b2" : "#a855f7",
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#1e293b" }}>{order.service_name}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={order.priority}
                        sx={{
                          height: 20,
                          fontSize: "0.68rem",
                          fontWeight: 800,
                          bgcolor: order.priority === "STAT" ? "#fee2e2" : order.priority === "Urgent" ? "#fef3c7" : "#f1f5f9",
                          color: order.priority === "STAT" ? "#b91c1c" : order.priority === "Urgent" ? "#b45309" : "#475569",
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.82rem", color: "#475569" }}>{order.doctor_name || "—"}</TableCell>
                    <TableCell sx={{ fontSize: "0.82rem", color: "#64748b" }}>{order.ordered_time || "—"}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={order.status}
                        sx={{
                          height: 22,
                          fontSize: "0.72rem",
                          fontWeight: 700,
                          bgcolor: isReported ? "#dcfce7" : isInProgress ? "#fef3c7" : "#e0f2fe",
                          color: isReported ? "#15803d" : isInProgress ? "#b45309" : "#0369a1",
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<VisibilityIcon sx={{ fontSize: 14 }} />}
                        onClick={() => setSelectedCpoeReport(order)}
                        sx={{ textTransform: "none", fontSize: "0.75rem", py: 0.2 }}
                      >
                        {order.type === "Laboratory" ? "View Parameters" : "View Report"}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Navigation Buttons */}
      {(onPrev || onNext) && (
        <Stack direction="row" justifyContent="space-between" sx={{ pt: 1 }}>
          {onPrev && (
            <Button variant="outlined" onClick={onPrev} sx={{ textTransform: "none" }}>
              Previous: Airway & Systemic
            </Button>
          )}
          {onNext && (
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={onNext}
              sx={{ bgcolor: "#0284c7", textTransform: "none", fontWeight: 700 }}
            >
              Next: Anesthesia Plan & Logistics
            </Button>
          )}
        </Stack>
      )}

      {/* Report Modal */}
      {selectedCpoeReport && (
        <Dialog open onClose={() => setSelectedCpoeReport(null)} maxWidth="md" fullWidth>
          <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", bgcolor: "#0f172a", color: "#fff" }}>
            <Stack direction="row" spacing={1} alignItems="center">
              {selectedCpoeReport.type === "Laboratory" ? <ScienceIcon sx={{ color: "#38bdf8" }} /> : <RadioIcon sx={{ color: "#c084fc" }} />}
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                {selectedCpoeReport.service_name} ({selectedCpoeReport.order_no})
              </Typography>
            </Stack>
            <IconButton size="small" onClick={() => setSelectedCpoeReport(null)} sx={{ color: "#fff" }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 3 }}>
            <Typography variant="caption" color="text.secondary">
              Department: {selectedCpoeReport.type} · Status: <strong>{selectedCpoeReport.status}</strong> · Doctor: {selectedCpoeReport.doctor_name} · Ordered: {selectedCpoeReport.ordered_time}
            </Typography>
            <Divider sx={{ my: 2 }} />

            {selectedCpoeReport.type === "Laboratory" ? (
              Array.isArray(selectedCpoeReport.results) && selectedCpoeReport.results.length > 0 ? (
                <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e2e8f0" }}>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: "#f8fafc" }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Parameter Name</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Observed Value</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Unit</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Reference Range</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Flag</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedCpoeReport.results.map((p, idx) => (
                        <TableRow key={idx}>
                          <TableCell sx={{ fontWeight: 600 }}>{p.parameter_name || p.name || p.test_name}</TableCell>
                          <TableCell sx={{ fontWeight: 800, color: p.abnormal ? "#b91c1c" : "#0f172a" }}>
                            {p.result_value || p.value || p.result || "—"}
                          </TableCell>
                          <TableCell>{p.unit || p.units || "—"}</TableCell>
                          <TableCell>{p.reference_range || p.biological_reference_interval || p.range || "—"}</TableCell>
                          <TableCell>
                            {p.abnormal ? (
                              <Chip size="small" label="Abnormal" color="error" sx={{ height: 18, fontSize: "0.65rem", fontWeight: 800 }} />
                            ) : (
                              <Chip size="small" label="Normal" color="success" sx={{ height: 18, fontSize: "0.65rem", fontWeight: 800 }} />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ p: 2, textAlign: "center", color: "text.secondary" }}>
                  No structured parameter values available yet for this laboratory order. Current status: {selectedCpoeReport.status}.
                </Box>
              )
            ) : (
              <Box sx={{ p: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>Radiological Findings:</Typography>
                <Typography variant="body2" sx={{ mb: 2, whiteSpace: "pre-wrap" }}>
                  {selectedCpoeReport.findings || selectedCpoeReport.results || "Findings pending radiology interpretation."}
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>Diagnostic Impression:</Typography>
                <Typography variant="body2" sx={{ mb: 2, whiteSpace: "pre-wrap", fontWeight: 600 }}>
                  {selectedCpoeReport.impression || "Impression pending final sign-off."}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Reporting Radiologist: {selectedCpoeReport.radiologist || selectedCpoeReport.doctor_name || "Consultant Radiologist"}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setSelectedCpoeReport(null)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
    </Stack>
  );
}
